import type { GanttLink, GanttRow, Conflict } from '../types';

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function toConflict(link: GanttLink, byTaskId: Map<string, GanttRow>): Conflict {
  return {
    linkId: link.id,
    predecessorTaskId: link.from,
    successorTaskId: link.to,
    predecessorTitle: byTaskId.get(link.from)?.title ?? '',
    successorTitle: byTaskId.get(link.to)?.title ?? '',
  };
}

/**
 * Pure, client-side check of whether moving `taskId` to `[newStart, newEnd]`
 * would violate any loaded finish-to-start dependency:
 *
 * - when the moved task is a **successor**, its predecessor must finish
 *   (+`lagDays`) on or before the new start;
 * - when it is a **predecessor**, its successor must start on or after the new
 *   finish (+`lagDays`).
 *
 * Only drives the accept/reject banner — it does not reschedule anything. The
 * actual cascade of dependents is the backend's responsibility on PATCH.
 */
export function detectScheduleConflicts(
  taskId: string,
  newStart: Date,
  newEnd: Date,
  links: GanttLink[],
  rows: GanttRow[],
): Conflict[] {
  const byTaskId = new Map<string, GanttRow>();
  for (const r of rows) {
    if (r.taskId) byTaskId.set(r.taskId, r);
  }

  const conflicts: Conflict[] = [];
  for (const link of links) {
    if (link.to === taskId) {
      const pred = byTaskId.get(link.from);
      if (pred?.endDate && addDays(pred.endDate, link.lagDays) > newStart) {
        conflicts.push(toConflict(link, byTaskId));
      }
    }
    if (link.from === taskId) {
      const succ = byTaskId.get(link.to);
      if (succ?.startDate && addDays(newEnd, link.lagDays) > succ.startDate) {
        conflicts.push(toConflict(link, byTaskId));
      }
    }
  }
  return conflicts;
}
